import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { logger } from '@/utils/common/logger';

export interface ModalThemeColors {
  overlay: string;
  modalBg: string;
  modalBorder: string;
  titleText: string;
  messageText: string;
  cancelBg: string;
  cancelBorder: string;
  cancelText: string;
  confirmBg: string;
  confirmText: string;
}

interface WebConfirmModalProps {
  visible: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  themeColors?: ModalThemeColors;
}

export const WebConfirmModal: React.FC<WebConfirmModalProps> = ({
  visible,
  title = 'Confirm',
  message,
  confirmText = 'Discard',
  cancelText = 'Keep Editing',
  onConfirm,
  onCancel,
  themeColors,
}) => {
  // Hooks must run before any early return so the hook count is stable when
  // `visible` toggles. Defaults derive from the live theme so the modal follows
  // light/dark; callers can still override via `themeColors`.
  const { colors, semantic } = useThemeColors();

  const c = useMemo<ModalThemeColors>(
    () =>
      themeColors ?? {
        overlay: semantic.background.overlay,
        modalBg: colors.card.bg,
        modalBorder: colors.card.border,
        titleText: colors.text.primary,
        messageText: colors.text.secondary,
        cancelBg: colors.glass.buttonBg,
        cancelBorder: colors.card.border,
        cancelText: colors.text.primary,
        confirmBg: semantic.status.error,
        confirmText: '#FFFFFF',
      },
    [themeColors, colors, semantic],
  );

  const themedStyles = useMemo(
    () =>
      StyleSheet.create({
        overlay: {
          ...baseStyles.overlay,
          backgroundColor: c.overlay,
        },
        modal: {
          ...baseStyles.modal,
          backgroundColor: c.modalBg,
          borderColor: c.modalBorder,
        },
        title: {
          ...baseStyles.title,
          color: c.titleText,
        },
        message: {
          ...baseStyles.message,
          color: c.messageText,
        },
        cancel: {
          ...baseStyles.cancel,
          backgroundColor: c.cancelBg,
          borderColor: c.cancelBorder,
        },
        confirm: {
          ...baseStyles.confirm,
          backgroundColor: c.confirmBg,
        },
        cancelText: {
          ...baseStyles.cancelText,
          color: c.cancelText,
        },
        confirmText: {
          ...baseStyles.confirmText,
          color: c.confirmText,
        },
      }),
    [c],
  );

  if (Platform.OS !== 'web' || !visible) return null;

  const overlay = (
    <View style={themedStyles.overlay}>
      <View style={themedStyles.modal}>
        <Text style={themedStyles.title}>{title}</Text>
        <Text style={themedStyles.message}>{message}</Text>
        <View style={baseStyles.actions}>
          <Hoverable hover="field" style={[baseStyles.button, themedStyles.cancel]} onPress={onCancel} testID="confirm-modal-cancel">
            <Text style={themedStyles.cancelText}>{cancelText}</Text>
          </Hoverable>
          <Hoverable hover="button" style={[baseStyles.button, themedStyles.confirm]} onPress={onConfirm} testID="confirm-modal-confirm">
            <Text style={themedStyles.confirmText}>{confirmText}</Text>
          </Hoverable>
        </View>
      </View>
    </View>
  );

  try {
    const g: any = globalThis as any;
    const ReactDOM = g?.require ? g.require('react-dom') : require('react-dom');
    const target = g?.document?.body;
    if (ReactDOM?.createPortal && target) {
      return ReactDOM.createPortal(overlay, target);
    }
  } catch (e) {
    logger.warn('confirmModalPortalRenderFailed', e);
  }

  return overlay;
};

const baseStyles = StyleSheet.create({
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 16,
    width: '100%',
    height: '100%',
  },
  modal: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 10,
    padding: 20,
    borderWidth: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  message: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  button: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  cancel: {
    borderWidth: 1,
  },
  confirm: {},
  cancelText: {
    fontWeight: '600',
  },
  confirmText: {
    fontWeight: '700',
  },
});
